import type { AdminTaskVersionInfo } from '@kyumo/common';
import { ChevronDown, GitBranch, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface VersionSelectorProps {
  /**
   * バージョン一覧
   */
  versions: AdminTaskVersionInfo[];
  /**
   * 現在選択中のバージョン番号
   */
  currentVersion: number;
  /**
   * バージョン変更時のコールバック
   */
  onVersionChange: (version: number) => void;
  /**
   * 読み込み中かどうか
   */
  isLoading?: boolean;
  /**
   * 無効化するかどうか
   */
  disabled?: boolean;
}

/**
 * 日付をフォーマット
 */
function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('ja-JP', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * バージョン切り替えセレクターコンポーネント（Web 管理アプリ用）
 *
 * - バージョンが1つの場合は非表示
 * - 各バージョンの作成日時を表示
 * - タスク実行中は選択を無効化
 */
export function VersionSelector({
  versions,
  currentVersion,
  onVersionChange,
  isLoading = false,
  disabled = false,
}: VersionSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  // モバイルでのドロップダウンの寄せ方向。トリガーボタンはタイトル文字数に応じて
  // 画面上の位置が変わるため、開くたびに画面外に出ない側へ動的に決める。
  const [mobileAlign, setMobileAlign] = useState<'left' | 'right'>('left');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 外部クリックでドロップダウンを閉じる
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 開いたときにトリガーボタンの位置から、ドロップダウンが画面外に出ない寄せ方向を決める
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function updateAlignment() {
      const rect = dropdownRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      const dropdownWidth = 200; // min-w-[200px] と一致させる
      const spaceOnRight = window.innerWidth - rect.left;
      setMobileAlign(spaceOnRight >= dropdownWidth ? 'left' : 'right');
    }

    updateAlignment();
    window.addEventListener('resize', updateAlignment);
    return () => {
      window.removeEventListener('resize', updateAlignment);
    };
  }, [isOpen]);

  // バージョン選択ハンドラ
  const handleVersionSelect = useCallback(
    (version: number) => {
      if (version !== currentVersion) {
        onVersionChange(version);
      }
      setIsOpen(false);
    },
    [currentVersion, onVersionChange],
  );

  // キーボードナビゲーション
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen((prev) => !prev);
    }
  }, []);

  // バージョンが1つ以下の場合は非表示
  if (versions.length <= 1) {
    return null;
  }

  // バージョンを降順でソート（最新バージョンが上）
  const sortedVersions = [...versions].sort((a, b) => b.version - a.version);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* トリガーボタン */}
      <button
        type="button"
        onClick={() => !disabled && !isLoading && setIsOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
        disabled={disabled || isLoading}
        className={`
          inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium
          transition-colors
          ${
            disabled || isLoading
              ? 'bg-muted text-muted-foreground cursor-not-allowed'
              : 'bg-muted text-foreground hover:bg-accent hover:text-foreground'
          }
        `}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <GitBranch className="w-4 h-4" />
        )}
        <span>v{currentVersion}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* ドロップダウンメニュー */}
      {isOpen && (
        <div
          className={`absolute top-full mt-1 z-50 min-w-[200px] py-1 bg-muted border border-border rounded-lg shadow-lg md:left-0 md:right-auto ${
            mobileAlign === 'left' ? 'left-0' : 'right-0'
          }`}
          role="listbox"
          aria-activedescendant={`version-${currentVersion}`}
          tabIndex={0}
        >
          {sortedVersions.map((version) => {
            const isSelected = version.version === currentVersion;
            return (
              <button
                key={version.version}
                id={`version-${version.version}`}
                type="button"
                onClick={() => handleVersionSelect(version.version)}
                className={`
                  w-full px-3 py-2 text-left text-sm flex items-center justify-between
                  transition-colors
                  ${
                    isSelected
                      ? 'bg-primary-500/20 text-primary-400'
                      : 'text-foreground hover:bg-accent hover:text-foreground'
                  }
                `}
                role="option"
                aria-selected={isSelected}
              >
                <span className="font-medium">v{version.version}</span>
                <span className="text-xs text-muted-foreground">
                  {formatDate(version.createdAt)}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
